import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { usersApi } from '../../api/users';
import CardItem from './CardItem';

const GalleryTab = ({ cards, allCards, loading, filters, setFilters, usedCardsInActiveGameweeks, cardsInCoachTraining, user, currentPage, setCurrentPage, itemsPerPage, onCardListed, openTradesMap }) => {
  const [galleryPublic, setGalleryPublic] = useState(user?.gallery_public || false);
  const [updatingGallery, setUpdatingGallery] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const totalPages = Math.ceil(allCards.length / itemsPerPage);

  // Carica lo stato iniziale di gallery_public dall'utente
  useEffect(() => {
    if (user?.gallery_public !== undefined) {
      setGalleryPublic(user.gallery_public);
    }
  }, [user?.gallery_public]);

  // Handler per toggle gallery pubblica/privata
  const handleToggleGalleryPublic = async () => {
    if (!user?.id) return;
    
    const newValue = !galleryPublic;
    setUpdatingGallery(true);
    
    try {
      const response = await usersApi.updateGalleryPublic(user.id, newValue);
      if (response?.success) {
        setGalleryPublic(newValue);
        // Aggiorna anche l'utente nel localStorage
        const updatedUser = { ...user, gallery_public: newValue };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error updating gallery visibility:', error);
      // Revert on error
      setGalleryPublic(!newValue);
    } finally {
      setUpdatingGallery(false);
    }
  };

  return (
    <>
      {/* Filters */}
      <div className="football-card glass-football p-6 mb-6">
        <div className="mobile-filters-header mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-yellow-400" />
            <h2 className="text-xl font-bold text-[#1e293b]">Filters</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="mobile-filters-toggle"
            >
              {filtersOpen ? 'Nascondi' : 'Mostra'}
            </button>
            <button
              onClick={handleToggleGalleryPublic}
              disabled={updatingGallery}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                galleryPublic ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 hover:bg-gray-600'
              } text-white ${updatingGallery ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={galleryPublic ? 'Gallery is public - click to make private' : 'Gallery is private - click to make public'}
            >
              {galleryPublic ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span>{updatingGallery ? 'Updating...' : (galleryPublic ? 'Public' : 'Private')}</span>
            </button>
          </div>
        </div>
        <div className={`mobile-filters-body ${filtersOpen ? 'is-open' : 'is-closed'}`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search player name..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="input-football w-full pl-10"
                />
              </div>
            </div>
            <select
              value={filters.rarity}
              onChange={(e) => setFilters({ ...filters, rarity: e.target.value })}
              className="input-football"
            >
              <option value="">All Rarities</option>
              <option value="basic">Basic</option>
              <option value="plus">Plus</option>
              <option value="rare">Rare</option>
              <option value="epic">Epic</option>
              <option value="unique">Unique</option>
            </select>
            <select
              value={filters.position}
              onChange={(e) => setFilters({ ...filters, position: e.target.value })}
              className="input-football"
            >
              <option value="">All Positions</option>
              <option value="GK">Goalkeeper</option>
              <option value="DEF">Defender</option>
              <option value="MID">Midfielder</option>
              <option value="ATT">Attacker</option>
            </select>
            <input
              type="text"
              placeholder="Nationality/Team"
              value={filters.nationality}
              onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
              className="input-football"
            />
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="football-spinner mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading cards...</p>
        </div>
      ) : (
        <>
          <div className="grid gap-0 justify-center md:justify-start profile-gallery-grid">
            {cards.map((card) => (
              <div key={`${card.id}-${card.rarity?.name ?? ''}`} className="profile-gallery-card-scaled">
                <CardItem 
                  card={card} 
                  isUsedInActiveGameweek={usedCardsInActiveGameweeks.has(Number(card.id))}
                  isInCoachTraining={cardsInCoachTraining?.has(Number(card.id))}
                  user={user}
                  onCardListed={onCardListed}
                  hasOpenTrade={!!openTradesMap[card.id]}
                  openTrade={openTradesMap[card.id]}
                />
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {allCards.length > itemsPerPage && (
            <div className="flex items-center justify-center space-x-4 mt-6">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                  currentPage === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-400 text-black hover:bg-yellow-500'
                }`}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Previous
              </button>
              <span className="text-gray-400 text-sm">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                  currentPage >= totalPages - 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-400 text-black hover:bg-yellow-500'
                }`}
              >
                Next
                <ChevronRight className="w-5 h-5 ml-1" />
              </button>
            </div>
          )}
        </>
      )}

      {!loading && cards.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No cards in your collection yet
        </div>
      )}
    </>
  );
};

export default GalleryTab;
